import React, { useState } from 'react';
import { Grid, Dialog, DialogTitle, DialogActions, DialogContent, colors, Button, Box, Typography, TextField } from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import Error from '../../../../components/Error/Error';
import SelectAsync from '../../../../components/Filters/UserPicker';
import { toast } from 'react-toastify';
import { headers } from '../../../../services/genericConstantes';
import { getCurrentUrl } from '../../../../services/ApiServices';
import { Sampler } from '../../../../models/sampler';
import { User } from '../../../../models/user';

type IFormSamplerPopup = {
	sampler: Sampler,
	isOpen: boolean,
	onClose: () => void | null,
	reloadDatas: () => void | null,
	cancelWithTimesIcon: boolean | false,
	buttonCancel: Boolean | false,
	buttonValidate: Boolean | false,
	labelConfirm: String | null,
	disabledButtonValidate: Boolean | false,
	disabledButtonCancel: Boolean | false,
	labelCancel: String | null
};

const FormSamplerPopup = ({ sampler, isOpen, onClose, reloadDatas, cancelWithTimesIcon, buttonCancel, buttonValidate, labelConfirm, disabledButtonValidate, disabledButtonCancel, labelCancel }: IFormSamplerPopup) => {
	// Ref pour suivre si le composant est monté
	const isMountedRef = React.useRef(true);
	const [error, setError] = useState<string | null>(null);
	const [user, setUser] = useState<User>({ value: '', label: '' });
	// Assurer que samplerNumber est correctement initialisé même si sampler est undefined
const [samplerNumber, setSamplerNumber] = React.useState({ value: sampler && sampler.samplerNumber ? sampler.samplerNumber : '', error: false });
	// Mettre à jour samplerNumber quand sampler change
	React.useEffect(() => {
		// Marquer le composant comme monté
		isMountedRef.current = true;
		
		if (sampler && sampler.samplerNumber !== undefined) {
			setSamplerNumber(prev => ({ ...prev, value: sampler.samplerNumber || '' }));
		}
		
		// Nettoyage : marquer le composant comme démonté lors du cleanup
		return () => {
			isMountedRef.current = false;
		};
	}, [sampler]);
	const isEdit = !!sampler?.id;
	const title = isEdit ? 'Modifier le code préleveur' : 'Ajouter un préleveur';


	// Fonction pour réinitialiser le formulaire et fermer la popup
	const resetAndClose = () => {
		// Réinitialiser l'état local d'abord
		setSamplerNumber({ value: '', error: false });
		setUser({ value: '', label: '' });
		setError(null);
		
		// Fermer la popup en dernier
		if (onClose) onClose();
	};
	
	// Fonction pour gérer la réussite d'une opération
	const handleSuccess = (message: string) => {
		toast.success(message, { position: "bottom-center", theme: "colored" });
		
		// Fermer d'abord
		resetAndClose();
		
		// Puis recharger les données avec un délai
		if (reloadDatas) {
			setTimeout(() => {
				if (isMountedRef.current && reloadDatas) reloadDatas();
			}, 100);
		}
	};
	
	const onValidate = async () => {
		if (
			(isEdit && !samplerNumber.value) ||
			(!isEdit && !(samplerNumber.value && (user.label !== '' && user.value !== '')))
		) {
			setError("L'un des champs est vide ou incorrect.");
			return;
		}

		let body = {};
		if (isEdit) {
			body = {
				samplerNumber: samplerNumber.value,
			};
			// Ne pas appeler resetAndClose() ici - on laisse updateGenotypingSampler le faire
			await updateGenotypingSampler(sampler.id, body);
		} else {
			body = {
				idUser: user.value,
				samplerNumber: samplerNumber.value,
			};
				await createGenotypingSampler(body);
			// Le resetAndClose et reloadDatas sont gérés dans createGenotypingSampler
		}
		// Ne rien faire d'autre ici - la fermeture et le rechargement sont gérés dans les fonctions spécifiques
	};

	const updateGenotypingSampler = async (id: number, body: any) => {
		const postOptions = {
			headers: headers(),
			method: 'PUT',
			body: JSON.stringify(body),
		};
		fetch(`${getCurrentUrl()}/phmanager/genotyping/samplers/${id}`, postOptions)
			.then((response) => response.json())
			.then((data) => {
					if (data.error) {
						toast.error(data.error, { position: "bottom-center", theme: "colored" });
					} else {
						// Utiliser la nouvelle fonction handleSuccess
						handleSuccess('Préleveur mise à jour');
					}
			})
			.catch((error: string) => {
				setError("Une erreur est survenue lors de la modification d'un préleveur.");
			});
	};

	const createGenotypingSampler = async (body: any) => {
		const postOptions = {
			headers: headers(),
			method: 'POST',
			body: JSON.stringify(body),
		};
		fetch(`${getCurrentUrl()}/phmanager/genotyping/samplers/`, postOptions)
			.then((response) => response.json())
			.then((data) => {
					if (data.error) {
						toast.error(data.error, { position: "bottom-center", theme: "colored" });
					} else {
						// Utiliser la nouvelle fonction handleSuccess
						handleSuccess('Préleveur créé');
					}
			})
			.catch((error: string) => {
				setError("Une erreur est survenue lors de la création d'un préleveur.");
			});
	}

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			data-testid="popupConfirmMui"
			sx={{
				'.MuiPaper-root': {
					borderRadius: '20px',
				},
			}}
		>
			{title || cancelWithTimesIcon ? (
				<Grid container>
					{title && <DialogTitle>{title}</DialogTitle>}
					{cancelWithTimesIcon && (
						<Button
							onClick={onClose}
							sx={{ width: '40px', height: '60px', backgroundColor: 'transparent' }}
							aria-label="Fermeture de la fenêtre"
						>
							<FaTimes size={18} color={colors.red.A700} />
						</Button>
					)}
				</Grid>
			) : null}
			<DialogContent>
				<Grid container spacing={1}>
					<>
						{error && <Error message={error} />}
						{isEdit ? (
							<Grid size={{xs: 12}} sx={{ justifyContent: "flex-start" }}>
								<Box display="flex" alignItems="center" height="100%">
									<Typography><b>Organisation : </b>{sampler.organisation}</Typography>
								</Box>
							</Grid>
						) : null}
						<Grid size={{xs: 12}} sx={{ justifyContent: "flex-start" }}>
							{isEdit ? (
								<Box display="flex" alignItems="center" height="100%">
									<Typography><b>Utilisateur : </b>{sampler.displayName}</Typography>
								</Box>
							) : (
								<SelectAsync
									url={`/phmanager/async-select-options/users-who-are-not-sampler/${sampler?.typePerson ? `?typePerson=${sampler?.typePerson}` : ''}`}
									name="user"
									placeholder="Utilisateur"
									label="Utilisateur"
									title="Utilisateur"
									value={user}
									onChange={(newValue: any) => {
										if (newValue) {
											setUser({ 
												value: newValue.value || '', 
												label: newValue.label || '' 
											});
										}
									}}
								/>
							)}
						</Grid>
						<Grid size={{xs: 12}} sx={{ justifyContent: "flex-start" }}>
							<TextField
								name="samplerNumber"
								placeholder="Code préleveur"
								label="Code préleveur"
								title="Code préleveur"
								required
								error={samplerNumber.error}
								value={samplerNumber.value}
								onChange={(e) => { setSamplerNumber({ value: e.target.value, error: false }) }}
							/>
						</Grid>
					</>
				</Grid>
			</DialogContent>
			<DialogActions>
				{buttonCancel && (
					<Button
						aria-label="Annuler l'action"
						color="secondary"
						onClick={onClose}
						disabled={disabledButtonCancel as boolean}
						variant="outlined"
					>
						{labelCancel || 'Annuler'}
					</Button>
				)}
				{buttonValidate && (
					<Button
						aria-label="Confirmer l'action"
						color="primary"
						type="submit"
						onClick={onValidate}
						disabled={disabledButtonValidate as boolean}
					>
						{labelConfirm || 'Confirmer'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default FormSamplerPopup;
